import { PerpMarketProgram } from "./programs/perp-market";
import { MARKETS, type MarketSymbol } from "./constants";
import { debug } from "@/lib/debug";

// Store dynamic market mappings (Binance symbol -> Nordex symbol)
const dynamicMarketMappings: Map<string, string> = new Map();

/**
 * Maps Binance price data to Nordex perpetual markets
 * Now supports both hardcoded and dynamic markets from API
 */
export class MarketMapper {
  /**
   * Initialize dynamic market mappings from API data
   * Call this when markets are loaded from the API
   */
  static initializeDynamicMappings(markets: Array<{ symbol: string; binanceSymbol?: string }>): void {
    dynamicMarketMappings.clear();
    markets.forEach(market => {
      if (market.binanceSymbol) {
        dynamicMarketMappings.set(market.binanceSymbol, market.symbol);
      }
    });
    debug.log('MarketMapper', `Initialized ${dynamicMarketMappings.size} dynamic market mappings`);
  }

  /**
   * Update market price from Binance ticker data
   * Oracle price is now provided separately from the backend
   */
  static updateMarketPrice(binanceSymbol: string, price: number, oraclePrice?: number): void {
    const perpMarket = this.findPerpMarketByBinanceSymbol(binanceSymbol);
    
    if (perpMarket) {
      try {
        // Update both currentPrice and oraclePrice (if provided)
        PerpMarketProgram.updateMarketPrice(perpMarket, price, oraclePrice);
      } catch (_error) {
        // Silently ignore - markets may not be initialized yet
        console.debug("Market not initialized yet:", perpMarket);
      }
    }
  }

  /**
   * Update oracle price for a market directly using Nordex market symbol
   * Now supports both hardcoded and dynamic market symbols
   */
  static updateOraclePrice(nordexSymbol: string, oraclePrice: number): void {
    try {
      PerpMarketProgram.updateOraclePrice(nordexSymbol, oraclePrice);
    } catch (_error) {
      console.debug("Failed to update oracle price:", nordexSymbol);
    }
  }

  /**
   * Find perpetual market by Binance symbol
   * Now checks both dynamic mappings and hardcoded MARKETS
   */
  static findPerpMarketByBinanceSymbol(binanceSymbol: string): string | null {
    // First check dynamic mappings (from API)
    const dynamicMatch = dynamicMarketMappings.get(binanceSymbol);
    if (dynamicMatch) {
      return dynamicMatch;
    }
    
    // Fallback to hardcoded MARKETS
    const entry = Object.entries(MARKETS).find(
      ([_, config]) => config.binanceSymbol === binanceSymbol
    );
    
    return entry ? entry[0] : null;
  }

  /**
   * Get Binance symbol from perpetual market
   * Now checks both dynamic mappings and hardcoded MARKETS
   */
  static getBinanceSymbol(perpMarket: string): string | null {
    // Check hardcoded MARKETS first
    if (perpMarket in MARKETS) {
      return MARKETS[perpMarket as MarketSymbol].binanceSymbol;
    }
    
    // Check dynamic mappings (reverse lookup)
    for (const [binanceSymbol, nordexSymbol] of dynamicMarketMappings.entries()) {
      if (nordexSymbol === perpMarket) {
        return binanceSymbol;
      }
    }
    
    return null;
  }

  /**
   * Convert Binance symbol to perpetual market symbol
   */
  static toPerpMarket(binanceSymbol: string): string | null {
    return this.findPerpMarketByBinanceSymbol(binanceSymbol);
  }

  /**
   * Get all Binance symbols that need to be tracked
   * Now includes both hardcoded and dynamic markets
   */
  static getAllBinanceSymbols(): string[] {
    const hardcodedSymbols = Object.values(MARKETS).map((config) => config.binanceSymbol);
    const dynamicSymbols = Array.from(dynamicMarketMappings.keys());
    
    // Combine and deduplicate
    return Array.from(new Set([...hardcodedSymbols, ...dynamicSymbols]));
  }

  /**
   * Update market statistics from Binance data
   */
  static updateMarketStats(
    binanceSymbol: string,
    stats: {
      volume24h?: number;
      priceChange24h?: number;
      priceChangePercent24h?: number;
    }
  ): void {
    const perpMarket = this.findPerpMarketByBinanceSymbol(binanceSymbol);
    
    if (perpMarket) {
      try {
        const updateData: Parameters<typeof PerpMarketProgram.updateMarketStats>[1] = {};
        
        if (stats.volume24h !== undefined) {
          updateData.volume24h = stats.volume24h;
        }
        
        if (stats.priceChange24h !== undefined) {
          updateData.priceChange24h = stats.priceChange24h;
        }
        
        if (stats.priceChangePercent24h !== undefined) {
          updateData.priceChangePercent24h = stats.priceChangePercent24h;
        }
        
        PerpMarketProgram.updateMarketStats(perpMarket, updateData);
      } catch (_error) {
        // Silently ignore - markets may not be initialized yet
        console.debug("Market not initialized yet:", perpMarket);
      }
    }
  }

  /**
   * Update funding rates periodically
   */
  static updateFundingRates(): void {
    PerpMarketProgram.updateFundingRates();
  }

  /**
   * Get market configuration
   * Now only works for hardcoded markets
   */
  static getMarketConfig(symbol: string) {
    if (symbol in MARKETS) {
      return MARKETS[symbol as MarketSymbol];
    }
    return null;
  }

  /**
   * Format market symbol for display
   */
  static formatMarketSymbol(symbol: string): string {
    // For dynamic markets, just return the symbol as-is
    const config = this.getMarketConfig(symbol);
    return config ? config.symbol : symbol;
  }

  /**
   * Get base currency symbol
   */
  static getBaseCurrency(symbol: string): string | null {
    const config = this.getMarketConfig(symbol);
    if (config) {
      return config.baseSymbol;
    }
    
    // For dynamic markets, extract from symbol (e.g., "BTC-USD" -> "BTC")
    const parts = symbol.split('-');
    return parts[0] || null;
  }

  /**
   * Get quote currency symbol
   */
  static getQuoteCurrency(symbol: string): string | null {
    const config = this.getMarketConfig(symbol);
    if (config) {
      return config.quoteSymbol;
    }
    
    // For dynamic markets, extract from symbol (e.g., "BTC-USD" -> "USD")
    const parts = symbol.split('-');
    return parts[1] || null;
  }
}

// Auto-update funding rates every minute
if (typeof window !== "undefined") {
  setInterval(() => {
    MarketMapper.updateFundingRates();
  }, 60000); // Every minute
}

